import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Youtube from "@tiptap/extension-youtube";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TextAlign from "@tiptap/extension-text-align";
// Imagen con handles de resize en las esquinas — extension community
// que reemplaza el Image default de TipTap. El user puede arrastrar
// para redimensionar y los attrs width/height quedan guardados en el
// HTML (asi el PDF tambien respeta el tamano).
import ImageResize from "tiptap-extension-resize-image";
import { ApiError } from "@/lib/api";
import { marked } from "marked";
import Heading from "@tiptap/extension-heading";
import { slugifyHeading } from "@/lib/slug";
import { mergeAttributes, Node as TiptapNode } from "@tiptap/core";
import Modal from "@/ui/Modal";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { useToast } from "@/ui/Toast";
import { fmtBytes } from "@/lib/formato";

// --- Tabla de comandos: celda/encabezado con atributo `comando` ---
// Se serializa como class="celda-comando" (parseHTML lo lee de vuelta). El
// visor (GuiaDetalle) detecta esa clase e inyecta el botón "Copiar" por
// columna. El sanitizador del backend permite `class` en td/th.
const atributosComando = {
  comando: {
    default: false,
    parseHTML: (el: HTMLElement) =>
      el.classList?.contains("celda-comando") ?? false,
    renderHTML: (attrs: { comando?: boolean }) =>
      attrs.comando ? { class: "celda-comando" } : {},
  },
};
const CeldaComando = TableCell.extend({
  addAttributes() {
    return { ...this.parent?.(), ...atributosComando };
  },
});
const EncabezadoComando = TableHeader.extend({
  addAttributes() {
    return { ...this.parent?.(), ...atributosComando };
  },
});

/** Maximo por imagen al subir al servidor (5MB). Antes era 1MB porque
 * iba inline base64; ahora va al server y el HTML solo guarda la URL. */
const MAX_IMG_BYTES = 5 * 1024 * 1024;
/** Maximo por archivo .md importado — los markdowns mas largos suelen
 * indicar que hay que cortarlos en varias guias. 500KB cubre cualquier
 * doc razonable de soporte. */
const MAX_MD_BYTES = 500 * 1024;

// marked config: GFM (tablas, strikethrough, autolinks) y line breaks
// simples (un salto de linea es <br>, no requiere doble enter). Esto
// matchea lo que un user razonable espera al pegar un .md.
marked.setOptions({
  gfm: true,
  breaks: true,
});

// Renderer custom: inyecta id="<slug>" en cada heading para que los
// enlaces de un indice (TOC autogenerado en muchos .md) funcionen.
// El sanitize del backend permite el atributo `id` validandolo como
// slug-style.
//
// OJO (marked v5+): el metodo `heading` debe usar `this.parser`, que es
// el parser que marked inyecta en el renderer ACTIVO al momento de
// parsear. NO sirve crear un `new marked.Renderer()` suelto ni bindear
// su metodo original: marked solo cablea `.parser` a su renderer interno,
// asi que una instancia propia deja `this.parser` undefined y revienta
// con "Cannot read properties of undefined (reading 'parseInline')".
marked.use({
  renderer: {
    heading(token) {
      const inner = this.parser.parseInline(token.tokens);
      const level = token.depth;
      const slug = slugifyHeading(token.text || "");
      const idAttr = slug ? ` id="${slug}"` : "";
      return `<h${level}${idAttr}>${inner}</h${level}>\n`;
    },
  },
});

/** ImageResize maneja el tamano y la alineacion en su NodeView (DOM
 * live), guardandolos en los attrs NO estandar `containerStyle`/
 * `wrapperStyle`. El problema: al serializar (`getHTML`) esos attrs
 * salen como `containerstyle="..."`/`wrapperstyle="..."`, que (a) el
 * sanitize del backend borra —en <img> solo permite src/alt/width/
 * height/style— y (b) no centran nada fuera del editor (HTML plano del
 * detalle/PDF), porque el centrado vive en un container que solo existe
 * dentro del NodeView. Resultado: el centrado se perdia al guardar.
 *
 * Fix: en `renderHTML` volcamos el estilo del container (width + margin)
 * a un `style` REAL en el <img>, que el sanitize si conserva (permite
 * margin/width/height) y que con `display:block` (ver index.css) centra
 * de verdad. Al reabrir, el parseHTML base reconstruye `containerStyle`
 * leyendo justo ese `style` (su fallback usa element.style.cssText), asi
 * que el editor vuelve a mostrarlo alineado. Tambien declaramos
 * width/height para que persistan en el atributo HTML correspondiente. */
const ImageResizeConPersistencia = ImageResize.extend({
  addAttributes() {
    const parent = (this.parent?.() ?? {}) as Record<string, Record<string, unknown>>;
    return {
      ...parent,
      // containerStyle es donde el NodeView guarda el layout (width +
      // margin de alineacion). El parseHTML base, al ver el attr `width`,
      // devuelve un containerStyle SIN margin -> se pierde la alineacion
      // al reabrir. Preferimos el `style` inline real (que SI trae el
      // margin que persistimos en renderHTML) para reconstruirla. El
      // NodeView re-sanitiza el valor al aplicarlo (deja width/height/
      // margin). Orden: containerstyle (copy/paste interno) > style > width.
      containerStyle: {
        ...parent.containerStyle,
        parseHTML: (el: HTMLElement) => {
          const cs = el.getAttribute("containerstyle");
          if (cs) return cs;
          const style = el.getAttribute("style");
          if (style) return style;
          const w = el.getAttribute("width");
          return w ? `width: ${w}px; height: auto;` : null;
        },
      },
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute("width"),
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (el) => el.getAttribute("height"),
        renderHTML: (attrs) => (attrs.height ? { height: attrs.height } : {}),
      },
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute("style"),
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    // El estilo de layout vive en containerStyle (p.ej. "width: 600px;
    // height: auto; cursor: pointer; margin: 0px auto;"). Lo pasamos a un
    // `style` estandar quitando `cursor` (no es CSS de layout y el
    // sanitize lo tira igual). margin: 0 auto = centrado; 0 auto 0 0 =
    // izquierda; 0 0 0 auto = derecha — todo estandar y persistible.
    const cs = String(node.attrs.containerStyle || "")
      .replace(/cursor:[^;]+;?/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    const attrs: Record<string, unknown> = { ...HTMLAttributes };
    delete attrs.containerStyle;
    delete attrs.wrapperStyle;
    if (cs) attrs.style = cs;
    else delete attrs.style;
    return ["img", mergeAttributes(this.options.HTMLAttributes, attrs)];
  },
});

/** Heading con atributo `id` persistente. Al importar un .md, el renderer
 * de marked emite <h2 id="slug"> para que el indice/TOC ancle a cada
 * seccion. Pero el schema base de TipTap NO declara `id`, asi que lo
 * descarta al parsear el HTML y `getHTML()` lo pierde — los enlaces
 * `#slug` quedaban sin destino. Declarandolo, el id sobrevive el
 * round-trip y queda en el HTML guardado (y por ende en el PDF). El
 * sanitize del backend ya valida `id` slug-style en h1/h2/h3. */
const HeadingConId = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute("id"),
        renderHTML: (attrs) => (attrs.id ? { id: attrs.id } : {}),
      },
    };
  },
});

/** Salto de página DEDICADO (distinto del separador <hr>, que es solo
 * línea divisoria). Se serializa como <div class="page-break"> — un
 * marcador limpio que: en el PDF fuerza el corte de hoja (CSS del
 * template), en el editor se muestra etiquetado, y en la vista publicada
 * se oculta (en web no hay páginas). El sanitize ya permite div+class. */
const PageBreak = TiptapNode.create({
  name: "pageBreak",
  group: "block",
  atom: true,
  selectable: true,
  parseHTML() {
    return [{ tag: "div.page-break" }];
  },
  renderHTML() {
    return ["div", { class: "page-break" }];
  },
});

/** Banderas "ajustar a una hoja". El usuario marca el INICIO y el FIN de
 * una región; en el PDF, todo lo que quede entre ellas se escala
 * proporcionalmente (letra + imágenes, en %) para caber en UNA sola hoja
 * (lo hace el renderer del servidor midiendo y escalando). Son marcadores
 * vacíos: <div class="fit-inicio"> y <div class="fit-fin">. En el editor
 * se ven etiquetados; en la vista publicada se ocultan (la web no pagina).
 * El sanitize ya permite div+class. */
const FitInicio = TiptapNode.create({
  name: "fitInicio",
  group: "block",
  atom: true,
  selectable: true,
  parseHTML() {
    return [{ tag: "div.fit-inicio" }];
  },
  renderHTML() {
    return ["div", { class: "fit-inicio" }];
  },
});
const FitFin = TiptapNode.create({
  name: "fitFin",
  group: "block",
  atom: true,
  selectable: true,
  parseHTML() {
    return [{ tag: "div.fit-fin" }];
  },
  renderHTML() {
    return ["div", { class: "fit-fin" }];
  },
});

type Props = {
  value: string; // HTML
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** Hoja del PDF exportado (se guarda con la guía). Si no vienen las
      dos props, el botón de orientación no se muestra. */
  orientacionPdf?: "horizontal" | "vertical";
  onOrientacionPdf?: (o: "horizontal" | "vertical") => void;
};

/**
 * Editor WYSIWYG DEDICADO a las Guias. Aparte del de correos a proposito:
 * agrega videos de YouTube embebidos inline sin tocar el editor compartido.
 * TipTap StarterKit + Link + Image inline + Youtube.
 */
export default function GuiaRichEditor({
  value,
  onChange,
  placeholder,
  minHeight = 260,
  orientacionPdf,
  onOrientacionPdf,
}: Props) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mdInputRef = useRef<HTMLInputElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [ytOpen, setYtOpen] = useState(false);
  const [ytUrl, setYtUrl] = useState("");
  // Acción de tabla pendiente de confirmar (borrar es destructivo).
  const [confirmAccion, setConfirmAccion] = useState<
    null | "col" | "fila" | "tabla"
  >(null);
  // Dimensiones en vivo mientras se redimensiona una imagen (badge).
  const [resizeBadge, setResizeBadge] = useState<
    { w: number; h: number; x: number; y: number } | null
  >(null);

  const editor = useEditor({
    extensions: [
      // Desactivamos el heading de StarterKit para usar HeadingConId
      // (mismo nodo + atributo `id` persistente para los anclas del TOC).
      StarterKit.configure({ heading: false }),
      HeadingConId.configure({ levels: [2, 3] }),
      PageBreak,
      FitInicio,
      FitFin,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      // ImageResize reemplaza al Image default — agrega los handles
      // visuales de resize en las esquinas y permite alinear izq/centro/
      // derecha. La version `ConPersistencia` extiende el schema para
      // que width/height/style se serialicen en el HTML guardado.
      ImageResizeConPersistencia,
      Youtube.configure({ nocookie: true, controls: true, width: 560, height: 315 }),
      // Tablas: importadas desde Markdown (| col | col |) o insertadas
      // manualmente con el boton de la toolbar. resizable=true permite
      // arrastrar el ancho de columnas.
      Table.configure({
        resizable: true,
        handleWidth: 8,
        cellMinWidth: 48,
        HTMLAttributes: { class: "tiptap-table" },
      }),
      TableRow,
      EncabezadoComando,
      CeldaComando,
      // Permite alinear (izq/centro/der/justificado) parrafos y headings.
      // El attr se guarda como `style="text-align: ..."` en el HTML — el
      // sanitize del backend lo permite explicitamente.
      TextAlign.configure({
        types: ["heading", "paragraph"],
        defaultAlignment: "left",
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class:
          "tiptap-content tiptap-editing max-w-none focus:outline-none px-3 py-2 text-sm text-zinc-100",
        style: `min-height: ${minHeight}px;`,
        "data-placeholder": placeholder ?? "Escribe aquí...",
      },
    },
  });

  // Sync externo -> editor (al abrir el modal con una guia existente).
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Indicador de dimensiones al redimensionar imagenes. La extension de
  // resize no muestra el tamano, asi que el ajuste era "al ojo" y costaba
  // mantener un estandar entre capturas. Enganchamos un listener en el DOM
  // del editor: al agarrar un handle de resize leemos el ancho/alto real
  // del <img> en vivo (rAF para no forzar layout por cada pixel) y lo
  // mostramos en un badge. No tocamos node_modules — el <img> es hermano
  // del handle dentro del container que arma el NodeView.
  useEffect(() => {
    if (!editor) return;
    const root = editor.view.dom as HTMLElement;
    let img: HTMLImageElement | null = null;
    let raf = 0;

    const measure = () => {
      raf = 0;
      if (!img) return;
      const r = img.getBoundingClientRect();
      setResizeBadge({
        w: Math.round(r.width),
        h: Math.round(r.height),
        x: r.left,
        y: r.top,
      });
    };
    const onMove = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    const stop = () => {
      img = null;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      setResizeBadge(null);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", stop);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", stop);
      document.removeEventListener("touchcancel", stop);
    };
    const onDown = (e: Event) => {
      const t = e.target as HTMLElement | null;
      const handle = t?.closest('[data-resize-image-ui="resize-handle"]');
      if (!handle) return;
      img = handle.parentElement?.querySelector("img") ?? null;
      if (!img) return;
      measure();
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", stop);
      document.addEventListener("touchmove", onMove, { passive: true });
      document.addEventListener("touchend", stop);
      document.addEventListener("touchcancel", stop);
    };

    root.addEventListener("mousedown", onDown, true);
    root.addEventListener("touchstart", onDown, true);
    return () => {
      root.removeEventListener("mousedown", onDown, true);
      root.removeEventListener("touchstart", onDown, true);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const insertImage = useCallback(
    async (file: File) => {
      if (!editor) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Ese archivo no es una imagen", file.type);
        return;
      }
      if (file.size > MAX_IMG_BYTES) {
        toast.error(
          `Imagen muy grande (${fmtBytes(file.size)})`,
          `Máximo ${fmtBytes(MAX_IMG_BYTES)} por imagen.`,
        );
        return;
      }
      try {
        // Upload server-side: la imagen va a /storage/guias_imagenes/
        // y el HTML guarda solo la URL. Antes era inline base64 (HTML
        // gordo, sin limite practico de resize). Ahora el PDF tambien
        // las puede embeber via file://.
        const form = new FormData();
        form.append("file", file);
        const resp = await fetch("/api/guias/imagenes", {
          method: "POST",
          body: form,
          credentials: "include",
        });
        if (!resp.ok) {
          const detail = await resp
            .json()
            .then((d) => d.detail)
            .catch(() => `HTTP ${resp.status}`);
          throw new ApiError(resp.status, String(detail));
        }
        const { url } = (await resp.json()) as { url: string };
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      } catch (err) {
        const msg = err instanceof ApiError ? err.detail : String(err);
        toast.error("No pude subir la imagen", msg);
      }
    },
    [editor, toast],
  );

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void insertImage(f);
    e.target.value = "";
  }

  /** Lee un archivo .md, convierte a HTML via `marked`, y lo inserta
   * en la posicion del cursor (NO reemplaza el contenido existente).
   *
   * El HTML resultante pasara por el sanitize del backend al guardar
   * — tags peligrosos se strip-ean. TipTap acepta el HTML directamente
   * con insertContent.
   */
  const importarMarkdown = useCallback(
    async (file: File) => {
      if (!editor) return;
      const nombre = file.name.toLowerCase();
      // Aceptamos .md y .markdown. .txt tambien lo procesamos como md
      // (es comun guardar markdown como .txt en algunos sistemas).
      const esMd =
        nombre.endsWith(".md") ||
        nombre.endsWith(".markdown") ||
        file.type === "text/markdown";
      const esTxt =
        nombre.endsWith(".txt") || file.type === "text/plain";
      if (!esMd && !esTxt) {
        toast.error(
          "Ese archivo no es Markdown",
          "Acepta .md, .markdown o .txt",
        );
        return;
      }
      if (file.size > MAX_MD_BYTES) {
        toast.error(
          `Archivo muy grande (${fmtBytes(file.size)})`,
          `Máximo ${fmtBytes(MAX_MD_BYTES)}. Considera partirlo en varias guías.`,
        );
        return;
      }
      try {
        const md = await file.text();
        let html = (await marked.parse(md)) as string;

        // Las imagenes con paths relativos (./img.png, img.png) no las
        // puede cargar el browser ni el backend (el sanitize solo deja
        // http/https/data:base64). Las detectamos, las reemplazamos por
        // un placeholder visible y avisamos al user para que arrastre
        // los archivos al editor.
        const imgsRelativos: string[] = [];
        html = html.replace(
          /<img([^>]*?)src=["']([^"']+)["']([^>]*)>/gi,
          (full, _pre, src, _post) => {
            if (/^(https?:|data:|\/api\/guias\/imagenes\/)/i.test(src)) {
              return full;
            }
            imgsRelativos.push(src);
            // Escapado: src viene del .md del usuario (audit fcfg-4).
            const srcEsc = src
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;");
            return (
              `<p><em style="color: #d97706;">` +
              `[falta imagen: ${srcEsc} — arrástrala aquí]` +
              `</em></p>`
            );
          },
        );

        editor.chain().focus().insertContent(html).run();

        if (imgsRelativos.length > 0) {
          toast.error(
            `Faltan ${imgsRelativos.length} imagen${imgsRelativos.length === 1 ? "" : "es"}`,
            `Paths relativos en el .md (ej. ${imgsRelativos[0]}). ` +
              `Arrastra los archivos al editor donde dice "[falta imagen: ...]" para subirlos.`,
          );
        } else {
          toast.success(
            "Markdown importado",
            `${file.name} insertado en el cursor`,
          );
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error("No pude convertir el markdown", msg);
      }
    },
    [editor, toast],
  );

  function onMdChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void importarMarkdown(f);
    e.target.value = "";
  }

  /** Decide que hacer con un archivo soltado/pegado: imagen va a
   * insertImage, .md/.markdown/.txt a importarMarkdown. Devuelve true
   * si maneja el archivo, false si lo ignora (para que el browser haga
   * lo que toque por default). */
  const dispatchArchivo = useCallback(
    (file: File): boolean => {
      if (file.type.startsWith("image/")) {
        void insertImage(file);
        return true;
      }
      const nombre = file.name.toLowerCase();
      const esMd =
        nombre.endsWith(".md") ||
        nombre.endsWith(".markdown") ||
        file.type === "text/markdown";
      const esTxt =
        nombre.endsWith(".txt") || file.type === "text/plain";
      if (esMd || esTxt) {
        void importarMarkdown(file);
        return true;
      }
      return false;
    },
    [insertImage, importarMarkdown],
  );

  // Drag-and-drop + paste: el user prefiere DnD por encima del picker
  // ([[drag-drop-archivos]]). Tanto imagenes como archivos .md se
  // sueltan directamente en el editor.
  const [isDragOver, setIsDragOver] = useState(false);
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    const onDragEnter = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
        setIsDragOver(true);
      }
    };
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
    };
    const onDragLeave = (e: DragEvent) => {
      // Quitar el highlight solo cuando salimos del editor completo;
      // dragleave dispara tambien al cruzar elementos hijos.
      if (!dom.contains(e.relatedTarget as Node)) setIsDragOver(false);
    };
    const onDrop = (e: DragEvent) => {
      setIsDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      if (dispatchArchivo(file)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file && dispatchArchivo(file)) {
            e.preventDefault();
            return;
          }
        }
      }
    };
    dom.addEventListener("dragenter", onDragEnter);
    dom.addEventListener("dragover", onDragOver);
    dom.addEventListener("dragleave", onDragLeave);
    dom.addEventListener("drop", onDrop);
    dom.addEventListener("paste", onPaste);
    return () => {
      dom.removeEventListener("dragenter", onDragEnter);
      dom.removeEventListener("dragover", onDragOver);
      dom.removeEventListener("dragleave", onDragLeave);
      dom.removeEventListener("drop", onDrop);
      dom.removeEventListener("paste", onPaste);
    };
  }, [editor, dispatchArchivo]);

  function abrirLinkModal() {
    if (!editor) return;
    setLinkUrl(editor.getAttributes("link").href || "");
    setLinkOpen(true);
  }

  function aplicarLink() {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().unsetLink().run();
    } else {
      const final = /^https?:|^mailto:/.test(url) ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange("link").setLink({ href: final }).run();
    }
    setLinkOpen(false);
  }

  function aplicarYoutube() {
    if (!editor) return;
    const url = ytUrl.trim();
    if (url) {
      const ok = editor.chain().focus().setYoutubeVideo({ src: url }).run();
      if (!ok) {
        toast.error("Dirección de YouTube no válida", "Pega el enlace de un video de YouTube.");
        return;
      }
    }
    setYtOpen(false);
    setYtUrl("");
  }

  if (!editor) {
    return (
      <div className="card text-sm text-zinc-500" style={{ minHeight }}>
        Cargando editor...
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-md border bg-ink-900 transition-colors ${
        isDragOver
          ? "border-accent-500/70 ring-2 ring-accent-500/30"
          : "border-ink-700"
      }`}
    >
      {/* Toolbar sticky: queda fija arriba al hacer scroll dentro del
          modal. El wrapper NO debe tener overflow-hidden o el sticky se
          ancla a el (que no scrollea) en vez del body del modal y la
          barra se pierde al bajar. rounded-t-md reemplaza el clip de
          esquinas que antes daba el overflow del wrapper. */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 rounded-t-md px-2 py-1.5 border-b border-ink-700 bg-ink-800 backdrop-blur supports-[backdrop-filter]:bg-ink-800/90">
        <Btn nombre="Negrita" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <span className="font-bold">B</span>
        </Btn>
        <Btn nombre="Itálica" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <span className="italic">I</span>
        </Btn>
        <Btn nombre="Tachado" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <span className="line-through">S</span>
        </Btn>
        <Sep />
        <Btn nombre="Título" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          H
        </Btn>
        <Btn nombre="Lista con viñetas" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          •
        </Btn>
        <Btn nombre="Lista numerada" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          1.
        </Btn>
        <Btn nombre="Cita" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          ❝
        </Btn>
        <Sep />
        <Btn nombre="Alinear izquierda" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          ⇤
        </Btn>
        <Btn nombre="Centrar" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          ↔
        </Btn>
        <Btn nombre="Alinear derecha" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          ⇥
        </Btn>
        <Btn nombre="Justificar" active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
          ☰
        </Btn>
        <Sep />
        <Btn nombre="Enlace" active={editor.isActive("link")} onClick={abrirLinkModal}>
          🔗
        </Btn>
        <Btn nombre="Insertar imagen (máx 5MB)" onClick={() => fileInputRef.current?.click()}>
          🖼
        </Btn>
        <Btn nombre="Insertar video de YouTube" onClick={() => { setYtUrl(""); setYtOpen(true); }}>
          ▶ YouTube
        </Btn>
        <Btn
          nombre="Insertar tabla"
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
        >
          ▦
        </Btn>
        <Btn
          nombre="Columna de comandos"
          active={
            !!editor.getAttributes(
              editor.isActive("tableHeader") ? "tableHeader" : "tableCell",
            ).comando
          }
          onClick={() => {
            const tipo = editor.isActive("tableHeader")
              ? "tableHeader"
              : "tableCell";
            const ya = !!editor.getAttributes(tipo).comando;
            editor.chain().focus().setCellAttribute("comando", !ya).run();
          }}
        >
          ⧉
        </Btn>
        {editor.isActive("table") && (
          <>
            <Sep />
            <Btn
              nombre="Insertar columna a la derecha"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
            >
              +col
            </Btn>
            <Btn
              nombre="Quitar la columna actual"
              onClick={() => setConfirmAccion("col")}
            >
              −col
            </Btn>
            <Btn
              nombre="Insertar fila abajo"
              onClick={() => editor.chain().focus().addRowAfter().run()}
            >
              +fila
            </Btn>
            <Btn
              nombre="Quitar la fila actual"
              onClick={() => setConfirmAccion("fila")}
            >
              −fila
            </Btn>
            <Btn
              nombre="Borrar toda la tabla"
              onClick={() => setConfirmAccion("tabla")}
            >
              🗑
            </Btn>
          </>
        )}
        <Btn
          nombre="Insertar separador"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          ―
        </Btn>
        <Btn
          nombre="Salto de página del PDF"
          onClick={() => editor.chain().focus().insertContent({ type: "pageBreak" }).run()}
        >
          ⤓
        </Btn>
        <Btn
          nombre="Ajustar a una hoja del PDF"
          onClick={() => {
            const { from, to, empty } = editor.state.selection;
            if (empty) {
              toast.error(
                "Selecciona una sección primero",
                "Marca con el cursor lo que quieres ajustar a una hoja y vuelve a hacer clic.",
              );
              return;
            }
            // Inserta la bandera de fin primero (posición mayor) para no
            // correr la posición de inicio.
            editor
              .chain()
              .focus()
              .insertContentAt(to, { type: "fitFin" })
              .insertContentAt(from, { type: "fitInicio" })
              .run();
          }}
        >
          ⤢ 1 hoja
        </Btn>
        {orientacionPdf && onOrientacionPdf && (
          <Btn
            nombre={
              orientacionPdf === "horizontal"
                ? "Hoja del PDF: horizontal (cambiar a vertical)"
                : "Hoja del PDF: vertical (cambiar a horizontal)"
            }
            onClick={() =>
              onOrientacionPdf(orientacionPdf === "horizontal" ? "vertical" : "horizontal")
            }
          >
            {orientacionPdf === "horizontal" ? "▭ PDF horizontal" : "▯ PDF vertical"}
          </Btn>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChosen} />
        <Sep />
        <Btn
          nombre="Importar archivo Markdown"
          onClick={() => mdInputRef.current?.click()}
        >
          ↑ .md
        </Btn>
        <input
          ref={mdInputRef}
          type="file"
          accept=".md,.markdown,.txt,text/markdown,text/plain"
          className="hidden"
          onChange={onMdChosen}
        />
        <Sep />
        <Btn nombre="Limpiar formato" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>
          ⌫
        </Btn>
      </div>

      <EditorContent editor={editor} />

      {/* Overlay sutil cuando se arrastra un archivo. Pointer-events
          none para que no interfiera con el drop nativo del editor. */}
      {isDragOver && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-ink-950/70 backdrop-blur-[1px]">
          <div className="rounded-lg border-2 border-dashed border-accent-500/60 bg-accent-500/5 px-6 py-3 text-sm text-accent-300">
            Suelta aquí para insertar (imagen o .md)
          </div>
        </div>
      )}

      {/* Badge con las dimensiones en vivo durante el resize de imagenes.
          Portal a body para que no lo recorte el overflow del editor ni lo
          desubique un `transform` de algun ancestro (position: fixed). */}
      {resizeBadge &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[9999] rounded bg-ink-950/90 px-1.5 py-0.5 font-mono text-[11px] font-medium text-accent-300 shadow-lg ring-1 ring-accent-500/40"
            style={{ left: resizeBadge.x + 6, top: resizeBadge.y + 6 }}
          >
            {resizeBadge.w} × {resizeBadge.h} px
          </div>,
          document.body,
        )}

      <Modal
        open={linkOpen}
        onOpenChange={(v) => !v && setLinkOpen(false)}
        title="Enlace"
        description="Pega o escribe la dirección. Vacío quita el enlace."
        size="sm"
        footer={
          <>
            <button onClick={() => setLinkOpen(false)} className="btn-ghost">Cancelar</button>
            <button onClick={aplicarLink} className="btn-primary">Aplicar</button>
          </>
        }
      >
        <input
          type="text"
          autoFocus
          className="input"
          value={linkUrl}
          placeholder="https://ejemplo.com"
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aplicarLink(); } }}
        />
      </Modal>

      <Modal
        open={ytOpen}
        onOpenChange={(v) => !v && setYtOpen(false)}
        title="Insertar video de YouTube"
        description="Pega el enlace del video. Se inserta donde tengas el cursor."
        size="sm"
        footer={
          <>
            <button onClick={() => setYtOpen(false)} className="btn-ghost">Cancelar</button>
            <button onClick={aplicarYoutube} className="btn-primary">Insertar</button>
          </>
        }
      >
        <input
          type="text"
          autoFocus
          className="input"
          value={ytUrl}
          placeholder="https://www.youtube.com/watch?v=..."
          onChange={(e) => setYtUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aplicarYoutube(); } }}
        />
      </Modal>

      <ConfirmDialog
        open={confirmAccion !== null}
        onOpenChange={(v) => {
          if (!v) setConfirmAccion(null);
        }}
        variant="danger"
        title={
          confirmAccion === "tabla"
            ? "Borrar tabla"
            : confirmAccion === "fila"
              ? "Quitar fila"
              : "Quitar columna"
        }
        description={
          confirmAccion === "tabla"
            ? "Se elimina toda la tabla y su contenido. Puedes deshacer con Ctrl+Z."
            : confirmAccion === "fila"
              ? "Se elimina la fila actual y su contenido. Puedes deshacer con Ctrl+Z."
              : "Se elimina la columna actual y su contenido. Puedes deshacer con Ctrl+Z."
        }
        confirmLabel={confirmAccion === "tabla" ? "Borrar" : "Quitar"}
        onConfirm={() => {
          const c = editor.chain().focus();
          if (confirmAccion === "tabla") c.deleteTable().run();
          else if (confirmAccion === "fila") c.deleteRow().run();
          else if (confirmAccion === "col") c.deleteColumn().run();
        }}
      />
    </div>
  );
}

function Sep() {
  return <span className="w-px h-5 bg-ink-700 mx-1" />;
}

/** Botón de la barra. `nombre` va en `aria-label` (no hay tooltip: qué hace
 *  cada botón lo explica la Ayuda de Guías, `components/ayuda/vistas/guias`). */
function Btn({
  children,
  active,
  nombre,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  nombre: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={nombre}
      onClick={onClick}
      className={`min-w-[28px] h-7 px-2 text-sm rounded transition ${
        active
          ? "bg-accent-500/20 text-accent-300 border border-accent-500/40"
          : "text-zinc-300 hover:text-white hover:bg-ink-700 border border-transparent"
      }`}
    >
      {children}
    </button>
  );
}
